import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { firstAllowedPath } from '@vce/shared';
import { Button, Card, Input } from '@/components/ui/primitives';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/app/auth-context';

export function LoginPage() {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) navigate(firstAllowedPath(user), { replace: true });
  }, [isAuthenticated, navigate, user]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const logged = await login(email, password);
      navigate(firstAllowedPath(logged));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Não foi possível entrar. Tente de novo.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)] px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="font-display text-4xl tracking-tight text-[var(--color-ink)] sm:text-5xl">
            catalyze.ai
          </h1>
          <p className="mt-3 text-sm text-[var(--color-muted)]">
            enriqueça, aprove, publique!
          </p>
        </div>

        <Card className="p-6 sm:p-8">
          <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
            <div>
              <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-[var(--color-body)]">
                E-mail
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-xs font-medium text-[var(--color-body)]"
              >
                Senha
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                minLength={8}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>

            {error ? (
              <p
                role="alert"
                className="rounded-[10px] border border-[color-mix(in_srgb,var(--color-error)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-error)_12%,transparent)] px-3 py-2 text-xs text-[var(--color-error)]"
              >
                {error}
              </p>
            ) : null}

            <Button type="submit" variant="primary" className="w-full cursor-pointer" disabled={isSubmitting}>
              {isSubmitting ? 'Entrando…' : 'Entrar'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
